import React, {useState, useCallback, useRef, useEffect, useMemo} from "react";
import {TextInput, useTranslate} from "react-admin";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const PRESCRIPTION_MAX = 1024;

const fieldLabel = {
    fontSize: 10,
    color: "#888",
    marginBottom: 2,
    display: "block",
    whiteSpace: "nowrap",
};

const viewValue = {
    fontSize: 13,
    lineHeight: 'normal',
    color: "#222",
    display: "block",
};

const inputStyle = {
    width: "100%",
    padding: "4px 6px",
    borderRadius: 4,
    border: "1px solid #ccc",
    fontSize: 13,
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
    height: "26px"
};

const EyeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 7C1 7 3 3 7 3C11 3 13 7 13 7C13 7 11 11 7 11C3 11 1 7 1 7Z"
              stroke="#555" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="7" cy="7" r="1.8" stroke="#555" strokeWidth="1.2"/>
    </svg>
);

const viewBtnStyle = {
    flexShrink: 0,
    width: 26,
    height: 26,
    padding: 0,
    border: "1px solid #ccc",
    borderRadius: 4,
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    right: "0",
    bottom: "0"
};

const PrescriptionModal = ({ label, prescription, onClose, t }) => (
    <div
        onClick={onClose}
        style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                background: "#fff",
                borderRadius: 8,
                padding: "20px 24px",
                minWidth: 300,
                maxWidth: 480,
                boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                maxHeight: "70vh",
                overflow: "auto"
            }}
        >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 10 }}>
                {label}
            </div>
            <div style={{ fontSize: 13, color: "#222", lineHeight: 1.6, wordBreak: "break-word" }}>
                {prescription || "-"}
            </div>
            <button
                onClick={onClose}
                style={{
                    marginTop: 16,
                    fontSize: 12,
                    padding: "4px 12px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#fff",
                    color: "#555",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "block",
                    marginLeft: "auto",
                }}
            >
                {t("resources.photoModal.close") || "Закрити"}
            </button>
        </div>
    </div>
);

export const PetInfoPanel = ({
                                 user,
                                 userDoctorType,
                                 petDescription,
                                 setPetDescription,
                                 prescription,
                                 setPrescription,
                                 dateFrom,
                                 setDateFrom,
                                 dateTo,
                                 setDateTo,
                                 isEditMode,
                                 setIsEditMode,
                                 onSave,
                                 onCancel,
                                 hasPets,
                                 handleGoBackToPatients,
                                 getDate
                             }) => {
    const t = useTranslate();
    const [prescriptionExpanded, setPrescriptionExpanded] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [petNameModalOpen, setPetNameModalOpen] = useState(false);
    const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);

    const parsedDateFrom = useMemo(() => {
        return dateFrom && dayjs(dateFrom).isValid() ? dayjs(dateFrom) : null;
    }, [dateFrom]);

    const parsedDateTo = useMemo(() => {
        return dateTo && dayjs(dateTo).isValid() ? dayjs(dateTo) : null;
    }, [dateTo]);

    const parsedDateFromRef = useRef(parsedDateFrom);
    useEffect(() => {
        parsedDateFromRef.current = parsedDateFrom;
    }, [dateFrom]);

    const handleDateFromChange = useCallback((value) => {
        if (!value || !dayjs(value).isValid()) {
            setDateFrom(null);
            return;
        }
        setDateFrom(dayjs(value).format("YYYY-MM-DD"));
    }, [setDateFrom]);

    const handleDateToChange = useCallback((value) => {
        if (!value || !dayjs(value).isValid()) {
            setDateTo(null);
            return;
        }
        if (parsedDateFromRef.current && dayjs(value).isBefore(parsedDateFromRef.current, "day")) {
            return;
        }
        setDateTo(dayjs(value).format("YYYY-MM-DD"));
    }, [setDateTo]);

    const handlePrescriptionChange = (e) => {
        if (e.target.value.length <= PRESCRIPTION_MAX) {
            setPrescription(e.target.value);
        }
    };

    const displayDateFrom = parsedDateFrom ? parsedDateFrom.format("DD.MM.YYYY") : null;
    const displayDateTo   = parsedDateTo   ? parsedDateTo.format("DD.MM.YYYY")   : null;

    const isTruncated = prescription && prescription.length > PRESCRIPTION_MAX;
    const displayedPrescription =
        prescriptionExpanded || !isTruncated
            ? prescription
            : prescription.slice(0, PRESCRIPTION_MAX) + "…";

    return (
        <>
            <div
                className="back-button"
                variant="text"
                size="icon"
            >
                <span className="back-button-text" onClick={handleGoBackToPatients}>
                     <img
                         src={process.env.PUBLIC_URL + "/back-arrow.svg"}
                         alt="back-arrow"
                         style={{width: 16, height: 16}}
                     />
                    {t(userDoctorType === "private" ? "resources.chat.private" : "resources.chat.public")}
                </span>
                {!collapsed && <div
                    className="pet-info-collapsed-wrap"
                    style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 12,
                        flexWrap: "nowrap",
                        borderRadius: 6,
                        width: '100%',
                        marginLeft: "auto"
                    }}
                >
                    <div className="pet-info-panel_wrap with-expanded">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 8,
                                flexWrap: "nowrap",
                                borderRadius: 6,
                                width: '100%'
                            }}
                        >
                            <div style={{
                                width: '100%',
                                maxWidth: "100%",
                                overflow: "hidden",
                                ...(!isEditMode && {
                                    borderRadius: 6,
                                    border: '1px solid #eee',
                                    padding: '4px 8px',
                                }),
                            }}>
                            <span style={fieldLabel}>
                                {t("resources.chat.petDescription")}
                            </span>
                                {isEditMode ? (
                                    <input
                                        value={petDescription}
                                        onChange={(e) => setPetDescription(e.target.value)}
                                        placeholder={t("resources.chat.petDescriptionPlaceholder")}
                                        style={inputStyle}
                                    />
                                ) : (
                                    <div style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 4
                                    }}>
                                    <span style={{...viewValue}} className="pet-prescription-text">
                                    {petDescription || "-"}
                                </span>
                                        {prescription && (
                                            <button
                                                onClick={() => setPetNameModalOpen(true)}
                                                title={t("resources.chat.view") || "Переглянути"}
                                                style={viewBtnStyle}
                                            >
                                                <EyeIcon/>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{
                                width: '100%',
                                maxWidth: "100%",
                                overflow: "hidden",
                                ...(!isEditMode && {
                                    borderRadius: 6,
                                    border: '1px solid #eee',
                                    padding: '4px 8px',
                                }),
                                display: collapsed ? 'block' : 'none'
                            }}>
                            <span style={fieldLabel}>
                                {t("resources.chat.prescription")}
                            </span>
                                {isEditMode ? (
                                    <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                                    <textarea
                                        value={prescription}
                                        onChange={handlePrescriptionChange}
                                        maxLength={PRESCRIPTION_MAX}
                                        style={{
                                            width: "100%",
                                            minHeight: 80,
                                            border: "1px solid #ccc",
                                            padding: "8px 10px",
                                            fontSize: 14,
                                            resize: "none",
                                        }}
                                    />

                                        <div
                                            style={{
                                                textAlign: "right",
                                                fontSize: 11,
                                                color: "#aaa",
                                                fontVariantNumeric: "tabular-nums",
                                            }}
                                        >
                                            {prescription.length}/{PRESCRIPTION_MAX}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 4
                                    }}>
                                    <span style={{...viewValue}} className="pet-prescription-text">
                                        {prescription ? (
                                            <>
                                                {displayedPrescription}
                                                {isTruncated && (
                                                    <button
                                                        onClick={() => setPrescriptionExpanded((p) => !p)}
                                                        style={{
                                                            marginLeft: 3,
                                                            fontSize: 9,
                                                            color: "#1976d2",
                                                            background: "none",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            padding: 0,
                                                            textDecoration: "underline",
                                                            fontFamily: "inherit",
                                                        }}
                                                    >
                                                        {prescriptionExpanded
                                                            ? t("resources.chat.showLess") || "згорнути"
                                                            : t("resources.chat.showMore") || "більше"}
                                                    </button>
                                                )}
                                            </>
                                        ) : "-"}
                                    </span>
                                        {prescription && (
                                            <button
                                                onClick={() => setPrescriptionModalOpen(true)}
                                                title={t("resources.chat.view") || "Переглянути"}
                                                style={viewBtnStyle}
                                            >
                                                <EyeIcon/>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {collapsed && <div className={`dates-wrapper ${isEditMode ? 'edit' : ''}`}>
                            <div style={{
                                width: '100%',
                                ...(!isEditMode && {
                                    borderRadius: 6,
                                    border: '1px solid #eee',
                                    padding: '4px 8px',
                                }),
                            }}>
                                <span style={fieldLabel}>
                                    {t("resources.chat.dateFrom")}
                                </span>
                                {isEditMode ? (
                                    <DatePicker
                                        value={parsedDateFrom}
                                        onChange={handleDateFromChange}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: {width: 150, height: "26px"},
                                            },
                                        }}
                                        format="DD.MM.YYYY"
                                    />
                                ) : (
                                    <span style={viewValue}>
                                        {displayDateFrom || "-"}
                                    </span>
                                )}
                            </div>

                            <div style={{
                                width: '100%',
                                ...(!isEditMode && {
                                    borderRadius: 6,
                                    border: '1px solid #eee',
                                    padding: '4px 8px',
                                }),
                            }}>
                                <span style={fieldLabel}>
                                    {t("resources.chat.dateTo")}
                                </span>
                                {isEditMode ? (
                                    <DatePicker
                                        value={parsedDateTo}
                                        onChange={handleDateToChange}
                                        minDate={parsedDateFrom || undefined}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: {width: 150, height: "26px"},
                                            },
                                        }}
                                        format="DD.MM.YYYY"
                                    />
                                ) : (
                                    <span style={viewValue}>
                                        {displayDateTo || "-"}
                                    </span>
                                )}
                            </div>
                        </div>}
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 6,
                        marginLeft: "auto",
                        paddingBottom: 2,
                    }} className="controls">
                        <button
                            style={{
                                fontSize: 12,
                                padding: "0",
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                background: "#fff",
                                color: "#555",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                whiteSpace: "nowrap",
                                width: "32px",
                                height: "32px",
                                minHeight: "32px"
                            }}
                            onClick={() => {
                                setIsEditMode(false)
                                setCollapsed(prev => !prev)
                            }}
                        >
                            <span className={!collapsed ? '' : 'expanded-pet-row'}>
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 11 L14 17 L20 11" stroke="currentColor" strokeWidth="2"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>}
            </div>
            <div className={`chat-header ${ !collapsed ? 'without-collapsed' : '' }`}>
                <div className="chat-header-left">
                    <span className="chat-header-left_name" title={user?.name || '-'}>{user?.name}</span>
                    <div>{user?.phone}</div>

                    {userDoctorType === 'private' ? <div>
                        {user?.chat?.updatedAt
                            ? getDate(user?.chat?.updatedAt)
                            : "-"}
                    </div> : <div style={{whiteSpace: "nowrap"}}>
                        {user?.publicChat?.updatedAt
                            ? getDate(user?.publicChat?.updatedAt)
                            : "-"}
                    </div>}
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {petNameModalOpen && (
                        <PrescriptionModal
                            label={t("resources.chat.petDescription")}
                            prescription={petDescription}
                            onClose={() => setPetNameModalOpen(false)}
                            t={t}
                        />
                    )}
                    {prescriptionModalOpen && (
                        <PrescriptionModal
                            label={t("resources.chat.prescription")}
                            prescription={prescription}
                            onClose={() => setPrescriptionModalOpen(false)}
                            t={t}
                        />
                    )}
                    {collapsed && <div className="pet-info-panel">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 12,
                                flexWrap: "nowrap",
                                borderRadius: 6,
                            }}
                            className="pet-info-panel__container"
                        >
                            <div className="pet-info-panel_wrap">
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 8,
                                        flexWrap: "nowrap",
                                        borderRadius: 6,
                                        width: '100%'
                                    }}
                                >
                                    <div style={{
                                        width: '100%',
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        ...(!isEditMode && {
                                            borderRadius: 6,
                                            border: '1px solid #eee',
                                            padding: '4px 8px',
                                        }),
                                    }}>
                                <span style={fieldLabel}>
                                    {t("resources.chat.petDescription")}
                                </span>
                                        {isEditMode ? (
                                            <input
                                                value={petDescription}
                                                onChange={(e) => setPetDescription(e.target.value)}
                                                placeholder={t("resources.chat.petDescriptionPlaceholder")}
                                                style={inputStyle}
                                            />
                                        ) : (
                                            <div style={{
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 4
                                            }}>
                                        <span style={{...viewValue}} className="pet-prescription-text">
                                        {petDescription || "-"}
                                    </span>
                                                {prescription && (
                                                    <button
                                                        onClick={() => setPetNameModalOpen(true)}
                                                        title={t("resources.chat.view") || "Переглянути"}
                                                        style={viewBtnStyle}
                                                    >
                                                        <EyeIcon/>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{
                                        width: '100%',
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        ...(!isEditMode && {
                                            borderRadius: 6,
                                            border: '1px solid #eee',
                                            padding: '4px 8px',
                                        }),
                                        display: collapsed ? 'block' : 'none'
                                    }}>
                                <span style={fieldLabel}>
                                    {t("resources.chat.prescription")}
                                </span>
                                        {isEditMode ? (
                                            <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                                        <textarea
                                            value={prescription}
                                            onChange={handlePrescriptionChange}
                                            maxLength={PRESCRIPTION_MAX}
                                            style={{
                                                width: "100%",
                                                minHeight: 80,
                                                border: "1px solid #ccc",
                                                padding: "8px 10px",
                                                fontSize: 14,
                                                resize: "none",
                                            }}
                                        />

                                                <div
                                                    style={{
                                                        textAlign: "right",
                                                        fontSize: 11,
                                                        color: "#aaa",
                                                        fontVariantNumeric: "tabular-nums",
                                                    }}
                                                >
                                                    {prescription.length}/{PRESCRIPTION_MAX}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 4
                                            }}>
                                        <span style={{...viewValue}} className="pet-prescription-text">
                                            {prescription ? (
                                                <>
                                                    {displayedPrescription}
                                                    {isTruncated && (
                                                        <button
                                                            onClick={() => setPrescriptionExpanded((p) => !p)}
                                                            style={{
                                                                marginLeft: 3,
                                                                fontSize: 9,
                                                                color: "#1976d2",
                                                                background: "none",
                                                                border: "none",
                                                                cursor: "pointer",
                                                                padding: 0,
                                                                textDecoration: "underline",
                                                                fontFamily: "inherit",
                                                            }}
                                                        >
                                                            {prescriptionExpanded
                                                                ? t("resources.chat.showLess") || "згорнути"
                                                                : t("resources.chat.showMore") || "більше"}
                                                        </button>
                                                    )}
                                                </>
                                            ) : "-"}
                                        </span>
                                                {prescription && (
                                                    <button
                                                        onClick={() => setPrescriptionModalOpen(true)}
                                                        title={t("resources.chat.view") || "Переглянути"}
                                                        style={viewBtnStyle}
                                                    >
                                                        <EyeIcon/>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {collapsed && <div className={`dates-wrapper ${isEditMode ? 'edit' : ''}`}>
                                    <div style={{
                                        width: '100%',
                                        ...(!isEditMode && {
                                            borderRadius: 6,
                                            border: '1px solid #eee',
                                            padding: '4px 8px',
                                        }),
                                    }}>
                                    <span style={fieldLabel}>
                                        {t("resources.chat.dateFrom")}
                                    </span>
                                        {isEditMode ? (
                                            <DatePicker
                                                value={parsedDateFrom}
                                                onChange={handleDateFromChange}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {width: 150, height: "26px"},
                                                    },
                                                }}
                                                format="DD.MM.YYYY"
                                            />
                                        ) : (
                                            <span style={viewValue}>
                                            {displayDateFrom || "-"}
                                        </span>
                                        )}
                                    </div>

                                    <div style={{
                                        width: '100%',
                                        ...(!isEditMode && {
                                            borderRadius: 6,
                                            border: '1px solid #eee',
                                            padding: '4px 8px',
                                        }),
                                    }}>
                                    <span style={fieldLabel}>
                                        {t("resources.chat.dateTo")}
                                    </span>
                                        {isEditMode ? (
                                            <DatePicker
                                                value={parsedDateTo}
                                                onChange={handleDateToChange}
                                                minDate={parsedDateFrom || undefined}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {width: 150, height: "26px"},
                                                    },
                                                }}
                                                format="DD.MM.YYYY"
                                            />
                                        ) : (
                                            <span style={viewValue}>
                                            {displayDateTo || "-"}
                                        </span>
                                        )}
                                    </div>
                                </div>}
                            </div>
                            <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 6,
                                marginLeft: "auto",
                                paddingBottom: 2,
                            }} className="controls">
                                {(collapsed && !isEditMode) &&
                                    <button
                                        style={{
                                            fontSize: 12,
                                            padding: "4px 8px",
                                            borderRadius: 4,
                                            border: "1px solid #ccc",
                                            background: "#fff",
                                            color: "#555",
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            whiteSpace: "nowrap",
                                            width: "32px",
                                            height: "32px",
                                            minHeight: "32px"
                                        }}
                                        onClick={() => setIsEditMode(true)}
                                    >
                                        <img
                                            src={process.env.PUBLIC_URL + "/edit.svg"}
                                            alt="Edit"
                                            style={{cursor: "pointer", width: 16, height: 16}}
                                        />
                                    </button>
                                }
                                <button
                                    style={{
                                        fontSize: 12,
                                        padding: "0",
                                        borderRadius: 4,
                                        border: "1px solid #ccc",
                                        background: "#fff",
                                        color: "#555",
                                        cursor: "pointer",
                                        fontFamily: "inherit",
                                        whiteSpace: "nowrap",
                                        width: "32px",
                                        height: "32px",
                                        minHeight: "32px"
                                    }}
                                    onClick={() => {
                                        setIsEditMode(false)
                                        setCollapsed(prev => !prev)
                                    }}
                                >
                                <span className={!collapsed ? '' : 'expanded-pet-row'}>
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 11 L14 17 L20 11" stroke="currentColor" strokeWidth="2"
                                              strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </span>
                                </button>
                            </div>
                        </div>
                        {isEditMode && (<div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 6,
                            marginLeft: "auto",
                            paddingBottom: 2
                        }}>
                            <>
                                <button
                                    onClick={onSave}
                                    style={{
                                        fontSize: 12,
                                        padding: "4px 10px",
                                        borderRadius: 4,
                                        border: "1px solid #1976d2",
                                        background: "#1976d2",
                                        color: "#fff",
                                        cursor: "pointer",
                                        fontFamily: "inherit",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {t("resources.chat.save")}
                                </button>
                                <button
                                    onClick={onCancel}
                                    style={{
                                        fontSize: 12,
                                        padding: "4px 10px",
                                        borderRadius: 4,
                                        border: "1px solid #ccc",
                                        background: "#fff",
                                        color: "#555",
                                        cursor: "pointer",
                                        fontFamily: "inherit",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {t("resources.chat.cancel") || "Скасувати"}
                                </button>
                            </>
                        </div>)}
                    </div>}
                </LocalizationProvider>
            </div>
        </>
    );
};